import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import SyncIcon from '@mui/icons-material/Sync'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import CloseIcon from '@mui/icons-material/Close'
import AlarmIcon from '@mui/icons-material/Alarm'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { createPreparationLot } from '@state/reducers/preparationLotReducer'
import {
  CompoundDto,
  CompoundProductionStepDto,
  CompoundProductionStepType,
  CompoundType,
  CreatePreparationLotDto,
  CreatePreparationLotItemDto,
  GeneratorElutionDto,
  PreparationDocumentDto,
  ProductBatchDto,
} from '@services/dtos'
import { compoundService } from '@services/CompoundService'
import { preparationLotService } from '@services/PreparationLotService'
import { productBatchService } from '@services/ProductBatchService'
import { preparationDocumentService } from '@services/PreparationDocumentService'
import { compoundProductionStepAssignmentService } from '@services/CompoundProductionStepAssignmentService'
import { MemberDto, MemberService } from '@services/api'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import { FormHeader } from './FormHeader'
import SelectField, {
  useSelectFieldStyles,
} from '@components/inputs/SelectField'
import moment from 'moment'
import { ColorInput } from '../inputs'
import { CERCLAGE_COLORS, MemberRoleEnumLabels } from '@utils/constants'
import InputField from '@components/inputs/InputField'
import DoseCalibratorStatus from '../DoseCalibratorStatus'
import { FilePreview } from '../file/FilePreview'
import { VolumeInput } from './GeneratorElutionForm'
import { WrapperFormContent } from './WrapperFormContent'

import { humanizeDurationSeconds } from '@utils/date.utils'
import { isNil } from 'lodash'
import { preparationLotSchema } from '@utils/schemas'

type Props = {
  onClose: () => void
  onPreviewChange?: (hasPreview: boolean) => void
}

const PreparationLotForm: React.FC<Props> = ({ onClose, onPreviewChange }) => {
  const isMobile = useMediaQuery('(max-width:899px)')
  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const member = user && 'role' in user ? (user as unknown as MemberDto) : null
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedCompound, setSelectedCompound] = useState<CompoundDto | null>(
    null,
  )
  const [availableElutions, setAvailableElutions] = useState<
    GeneratorElutionDto[]
  >([])
  const [loadingElutions, setLoadingElutions] = useState(false)
  const [documents, setDocuments] = useState<PreparationDocumentDto[]>([])
  const [loadingDocuments, setLoadingDocuments] = useState(false)
  const [productionSteps, setProductionSteps] = useState<
    CompoundProductionStepDto[]
  >([])
  const [loadingSteps, setLoadingSteps] = useState(false)
  const [previewDocument, setPreviewDocument] =
    useState<PreparationDocumentDto | null>(null)
  const [availableBatches, setAvailableBatches] = useState<ProductBatchDto[]>(
    [],
  )
  const [loadingBatches, setLoadingBatches] = useState(false)
  const [batchStorageLocations, setBatchStorageLocations] = useState<
    { id: string; name: string; units: number }[]
  >([])

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<CreatePreparationLotDto>({
    resolver: yupResolver(preparationLotSchema),
    defaultValues: { performedById: member?.id, sourceElutionItems: [] },
  })

  const openPreview = (doc: PreparationDocumentDto) => {
    setPreviewDocument(doc)
    onPreviewChange?.(true)
  }

  const closePreview = () => {
    setPreviewDocument(null)
    onPreviewChange?.(false)
  }

  const watchedSourceBatchId = watch('sourceBatchId')

  useEffect(() => {
    if (!watchedSourceBatchId) {
      setBatchStorageLocations([])
      setValue('storageLocationId', undefined)
      return
    }
    const batch = availableBatches.find(({ id }) => id === watchedSourceBatchId)
    if (!batch) {
      return
    }
    const locationsWithStock = batch.storageLocations
      .filter(({ units }) => units > 0)
      .map(({ storageLocation, units }) => ({
        id: storageLocation.id,
        name: storageLocation.name,
        units,
      }))
    setBatchStorageLocations(locationsWithStock)
    setValue(
      'storageLocationId',
      locationsWithStock.length === 1 ? locationsWithStock[0].id : undefined,
    )
  }, [watchedSourceBatchId, availableBatches])

  const watchedItems = watch('sourceElutionItems') ?? []

  const dilutionStepsTotal = productionSteps
    .filter((s) => s.type === CompoundProductionStepType.DILUTION)
    .reduce((sum, s) => sum + (s.dilutionVolumeMl ?? 0), 0)

  const totalWatchedVolume = watchedItems.reduce((sum, item) => sum + (Number(item?.elutionVolumeMl) || 0), 0)

  useEffect(() => {
    watchedItems.forEach((item, idx) => {
      const elution = availableElutions.find((e) => e.id === item?.elutionId)
      if (!elution) {
        return
      }

      const maxActivity = elution.activityAtTimeMbq ?? elution.activityAtRefMbq
      if (!isNil(item.activityAtRefMbq) && item.activityAtRefMbq > maxActivity) {
        setError(`sourceElutionItems.${idx}.activityAtRefMbq`, { type: 'max', message: `Max ${maxActivity.toFixed(2)} MBq` })
      } else {
        clearErrors(`sourceElutionItems.${idx}.activityAtRefMbq`)
      }

      if (elution.remainingVolumeMl !== null && !isNil(item.elutionVolumeMl) && item.elutionVolumeMl > elution.remainingVolumeMl) {
        setError(`sourceElutionItems.${idx}.elutionVolumeMl`, { type: 'max', message: `Max ${elution.remainingVolumeMl} mL` })
      } else {
        clearErrors(`sourceElutionItems.${idx}.elutionVolumeMl`)
      }
    })
  }, [JSON.stringify(watchedItems), availableElutions])

  const fetchElutions = useCallback((compoundId: string) => {
    preparationLotService
      .getAvailableElutions(compoundId)
      .then(setAvailableElutions)
      .catch(() => setAvailableElutions([]))
      .finally(() => setLoadingElutions(false))
  }, [])

  const elutionIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!selectedCompound) {
      setAvailableElutions([])
      setValue('sourceElutionItems', [])
      setDocuments([])
      setProductionSteps([])
      setAvailableBatches([])
      setBatchStorageLocations([])
      setValue('sourceBatchId', undefined)
      setValue('storageLocationId', undefined)
      closePreview()
      if (elutionIntervalRef.current) {
        clearInterval(elutionIntervalRef.current)
        elutionIntervalRef.current = null
      }
      return
    }

    setLoadingElutions(true)
    fetchElutions(selectedCompound.id)

    if (elutionIntervalRef.current) {
      clearInterval(elutionIntervalRef.current)
    }
    elutionIntervalRef.current = setInterval(() => {
      fetchElutions(selectedCompound.id)
    }, 30_000)

    if (selectedCompound.coldKit?.id) {
      setLoadingBatches(true)
      setValue('sourceBatchId', undefined)
      productBatchService
        .getAll({ productCatalogId: selectedCompound.coldKit.id, inStock: true, limit: 2, page: 0 })
        .then((res) => setAvailableBatches(res?.datas ?? []))
        .catch(() => setAvailableBatches([]))
        .finally(() => setLoadingBatches(false))
    } else {
      setAvailableBatches([])
      setValue('sourceBatchId', undefined)
    }

    setLoadingDocuments(true)
    preparationDocumentService
      .getAll({ compoundId: selectedCompound.id, limit: 50, page: 0 })
      .then((res) => setDocuments(res?.datas ?? []))
      .catch(() => setDocuments([]))
      .finally(() => setLoadingDocuments(false))

    setLoadingSteps(true)
    compoundProductionStepAssignmentService
      .getAll({ compoundId: selectedCompound.id, limit: 10, page: 0 })
      .then((res) => {
        const assignment = res?.datas?.[0]
        setProductionSteps(assignment?.steps ?? [])
      })
      .catch(() => setProductionSteps([]))
      .finally(() => setLoadingSteps(false))

    return () => {
      if (elutionIntervalRef.current) {
        clearInterval(elutionIntervalRef.current)
        elutionIntervalRef.current = null
      }
    }
  }, [selectedCompound])

  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  const fetchCompounds = async (search: string) => {
    const response = await compoundService.getAll({
      search,
      limit: 20,
      page: 0,
      type: CompoundType.TO_PREPARE,
    })
    return response?.datas || []
  }

  const onSubmitForm = async (data: CreatePreparationLotDto) => {
    if (!data.compoundId) {
      return
    }
    setIsSubmitting(true)
    dispatch(createPreparationLot(data))
      .unwrap()
      .then(() => {
        dispatch(
          enqueueSnackbar({
            message: 'Préparation créée avec succès',
            options: { variant: 'success' },
          }),
        )
        onClose()
      })
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            message: 'Erreur lors de la création de la préparation',
            options: { variant: 'error' },
          }),
        ),
      )
      .finally(() => setIsSubmitting(false))
  }

  const isLoadingProtocol = loadingDocuments || loadingSteps

  const formFields = (
    <Grid container rowSpacing={2}>
      <Grid item xs={12}>
        <DoseCalibratorStatus />
      </Grid>

      <Grid item xs={12}>
        <ServerAutocomplete<CompoundDto>
          fetchService={fetchCompounds}
          onChange={(value) => {
            setSelectedCompound(value)
            setValue('compoundId', value?.id ?? '')
            setValue('sourceElutionItems', [])
          }}
          getValue={(value) => value?.id?.toString() || null}
          getOptionLabel={(value) => {
            const compound = value as CompoundDto
            return compound
              ? `${compound.name}${
                  compound.isotopes.length > 0
                    ? ` - ${compound.isotopes
                        .map(({ code }) => code)
                        .join(', ')}`
                    : ''
                }`
              : ''
          }}
          label="MRP (à préparer)"
          placeholder="Rechercher un MRP..."
          debounceMs={300}
          minSearchLength={0}
          autoFetchOnMount
          error={!!errors.compoundId}
          helperText={errors.compoundId?.message}
        />
      </Grid>

      {selectedCompound && (
        <>
          <Grid item xs={12}>
            <Accordion defaultExpanded={false} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Étapes de production
                  </Typography>
                  {isLoadingProtocol ? (
                    <CircularProgress size={12} />
                  ) : (
                    <Chip
                      label={productionSteps.length}
                      size="small"
                      color={productionSteps.length > 0 ? 'primary' : 'default'}
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                {loadingSteps ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 1 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                ) : productionSteps.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 1 }}
                  >
                    Aucune étape configurée pour ce MRP
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {productionSteps.map((step, index) => (
                      <ListItem key={step.id} sx={{ px: 0, py: 0.25 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              <Box
                                component="span"
                                sx={{ fontWeight: 500, mr: 0.5 }}
                              >
                                {index + 1}.
                              </Box>
                              {step.type ===
                                CompoundProductionStepType.TIMER && (
                                <AlarmIcon
                                  sx={{
                                    fontSize: 14,
                                    color: 'text.secondary',
                                    mr: 0.5,
                                    marginBottom: 0.5,
                                  }}
                                />
                              )}
                              {step.label}
                              {step.type ===
                              CompoundProductionStepType.BOOLEAN ? (
                                <Box component="span" color="text.secondary">
                                  {' '}
                                  - {step.expected ? 'Oui' : 'Non'}
                                </Box>
                              ) : step.type ===
                                CompoundProductionStepType.DILUTION ? (
                                <Box component="span" color="text.secondary">
                                  {' '}
                                  - {step.dilutionVolumeMl} mL
                                </Box>
                              ) : step.type ===
                                CompoundProductionStepType.TIMER ? (
                                <Box component="span" color="text.secondary">
                                  {' '}
                                  -{' '}
                                  {humanizeDurationSeconds(
                                    step.timerDurationSeconds,
                                  )}
                                </Box>
                              ) : step.expectedValue ? (
                                <Box component="span" color="text.secondary">
                                  {' '}
                                  - {step.expectedValue}
                                  {step.unit ? ` ${step.unit}` : ''}
                                </Box>
                              ) : null}
                            </Typography>
                          }
                          secondary={step.comment ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {step.comment}
                            </Typography>
                          ) : undefined}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion defaultExpanded={false} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Documents de préparation
                  </Typography>
                  {isLoadingProtocol ? (
                    <CircularProgress size={12} />
                  ) : (
                    <Chip
                      label={documents.length}
                      size="small"
                      color={documents.length > 0 ? 'primary' : 'default'}
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                {loadingDocuments ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 1 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                ) : documents.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 1 }}
                  >
                    Aucun document associé à ce MRP
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {documents.map((doc) => (
                      <ListItem
                        key={doc.id}
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          cursor: 'pointer',
                          bgcolor:
                            previewDocument?.id === doc.id
                              ? 'action.selected'
                              : 'transparent',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() =>
                          isMobile
                            ? window.open(doc.attachment.url, '_blank')
                            : previewDocument?.id === doc.id
                            ? closePreview()
                            : openPreview(doc)
                        }
                        secondaryAction={
                          <Tooltip title="Consulter">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                isMobile
                                  ? window.open(doc.attachment.url, '_blank')
                                  : previewDocument?.id === doc.id
                                  ? closePreview()
                                  : openPreview(doc)
                              }}
                            >
                              {!isMobile && previewDocument?.id === doc.id ? (
                                <CloseIcon fontSize="small" />
                              ) : (
                                <VisibilityOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2">{doc.label}</Typography>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {doc.attachment.filename}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <WrapperFormContent legend={'Élution(s)'}>
          {loadingElutions ? (
            <Box className="flex items-center gap-2 py-2">
              <CircularProgress size={16} />
              <Typography variant="body2">
                Chargement des élutions...
              </Typography>
            </Box>
          ) : !selectedCompound ? (
            <Alert severity="info">{'Sélectionnez d\'abord un MRP'}</Alert>
          ) : availableElutions.length === 0 ? (
            <Alert severity="error">
              Aucune élution disponible pour ce MRP
            </Alert>
          ) : (
            <>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Activité et volume mis à jour toutes les 30s
                </Typography>
                <Tooltip
                  title="L'activité et le volume restant sont recalculés par décroissance radioactive toutes les 30s"
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[...availableElutions].sort((a, b) => moment(b.activityRefAt).diff(moment(a.activityRefAt))).map((elution) => {
                  const isotope = elution.generatorElutionInstances?.[0]?.generatorInstance?.productBatch?.productCatalog?.generatorDaughterIsotope
                  const isOverAge = !!selectedCompound?.eluateMaxAgeMinutes
                    && moment().diff(moment(elution.activityRefAt), 'minutes') > selectedCompound.eluateMaxAgeMinutes
                  const itemIndex = watchedItems.findIndex((item) => item?.elutionId === elution.id)
                  const isSelected = itemIndex !== -1
                  const itemErrors = errors.sourceElutionItems?.[itemIndex]

                  const toggleElution = () => {
                    if (isSelected) {
                      setValue('sourceElutionItems', watchedItems.filter((_, i) => i !== itemIndex))
                    } else {
                      const newItem: CreatePreparationLotItemDto = {
                        elutionId: elution.id,
                        activityAtRefMbq: undefined,
                        elutionVolumeMl: undefined,
                        activityRefAt: new Date(),
                      }
                      setValue('sourceElutionItems', [...watchedItems, newItem])
                    }
                  }

                  return (
                    <Box
                      key={elution.id}
                      sx={{
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        transition: 'border-color 0.15s',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={toggleElution}
                      >
                        <Checkbox checked={isSelected} size="small" sx={{ p: 0.5 }} />
                        <Box sx={{ flex: 1, ml: 0.5 }}>
                          <Typography variant="body2" component="span" fontWeight={500}>
                            #{elution.no}
                          </Typography>
                          <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                            {moment(elution.activityRefAt).format('DD/MM/YYYY HH:mm')}
                            {isotope && ` · ${isotope.code}`}
                            {' · '}
                            <strong>{(elution.activityAtTimeMbq ?? elution.activityAtRefMbq).toFixed(2)} MBq</strong>
                            {elution.remainingVolumeMl !== null && ` · ${elution.remainingVolumeMl} mL`}
                          </Typography>
                        </Box>
                        {isOverAge && (
                          <Tooltip title={`Fraîcheur dépassée (max ${selectedCompound.eluateMaxAgeMinutes} min)`} arrow>
                            <WarningAmberIcon fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                      </Box>

                      {isSelected && (
                        <Box sx={{ px: 2, pb: 1.5, pt: 0.5, bgcolor: 'grey.50', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                          <InputField
                            control={control}
                            name={`sourceElutionItems.${itemIndex}.activityAtRefMbq`}
                            label="Activité (MBq)"
                            type="number"
                            inputProps={{ step: '0.01', min: 0 }}
                            sx={{ width: 140 }}
                          />
                          <VolumeInput
                            value={watchedItems[itemIndex]?.elutionVolumeMl ?? 0}
                            onChange={(val) => setValue(`sourceElutionItems.${itemIndex}.elutionVolumeMl`, val || 0)}
                            errorMessage={itemErrors?.elutionVolumeMl?.message}
                          />
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <TimePicker
                              label="Heure mesure"
                              disableFuture
                              value={watchedItems[itemIndex]?.activityRefAt ? moment(watchedItems[itemIndex].activityRefAt) : null}
                              onChange={(newValue) => {
                                if (!newValue?.isValid()) {
                                  setValue(`sourceElutionItems.${itemIndex}.activityRefAt`, undefined)
                                  return
                                }
                                setValue(`sourceElutionItems.${itemIndex}.activityRefAt`, moment().set({
                                  hour: newValue.hour(),
                                  minute: newValue.minute(),
                                  second: 0,
                                  millisecond: 0,
                                }).toDate())
                              }}
                              ampm={false}
                              views={['hours', 'minutes']}
                              timeSteps={{ minutes: 1 }}
                              slotProps={{
                                textField: {
                                  sx: { '& .MuiInputBase-input': { width: 52 } },
                                  error: !!itemErrors?.activityRefAt,
                                  helperText: itemErrors?.activityRefAt?.message,
                                  InputProps: {
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <Tooltip title="Heure actuelle" arrow>
                                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setValue(`sourceElutionItems.${itemIndex}.activityRefAt`, new Date()) }}>
                                            <SyncIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </InputAdornment>
                                    ),
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Box>

              {watchedItems.length > 1 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Total sélectionné : <strong>{watchedItems.reduce((s, i) => s + (Number(i?.activityAtRefMbq) || 0), 0).toFixed(2)} MBq</strong>
                  {' · '}
                  <strong>{totalWatchedVolume.toFixed(1)} mL</strong>
                </Alert>
              )}
            </>
          )}
        </WrapperFormContent>
      </Grid>

      {dilutionStepsTotal > 0 && totalWatchedVolume > 0 && (
        <Grid item xs={12}>
          <Alert severity="info">
            Volume final : <strong>{(totalWatchedVolume + dilutionStepsTotal).toFixed(2)} mL</strong>
            {' '}({totalWatchedVolume.toFixed(2)} mL élution + {dilutionStepsTotal.toFixed(2)} mL dilution)
          </Alert>
        </Grid>
      )}

      {selectedCompound?.coldKit && (
        <Grid item xs={12}>
          {loadingBatches ? (
            <Box className="flex items-center gap-2 py-2">
              <CircularProgress size={16} />
              <Typography variant="body2">Chargement du stock...</Typography>
            </Box>
          ) : availableBatches.length === 0 ? (
            <Alert severity="warning">
              {`Aucun stock disponible pour ${selectedCompound.coldKit.name}`}
            </Alert>
          ) : (
            <>
              <SelectField
                control={control}
                name="sourceBatchId"
                label={`Kit froid (${selectedCompound.coldKit.name})`}
                options={availableBatches.map((batch) => ({
                  value: batch.id,
                  label: `Lot ${batch.lotNumber} · reste ${
                    batch.totalUnits
                  } · exp. ${moment(batch.expiryAt).format('DD/MM/YYYY')}`,
                }))}
              />
              {batchStorageLocations.length === 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Emplacement : <strong>{batchStorageLocations[0].name}</strong>
                </Typography>
              )}
              {batchStorageLocations.length > 1 && (
                <Box mt={1}>
                  <SelectField
                    control={control}
                    name="storageLocationId"
                    label="Emplacement de stockage"
                    options={batchStorageLocations.map((loc) => ({
                      value: loc.id,
                      label: `${loc.name} · ${loc.units} unité${
                        loc.units > 1 ? 's' : ''
                      }`,
                    }))}
                    error={!!errors.storageLocationId}
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
      )}

      <Grid item xs={12}>
        <ServerAutocomplete<MemberDto>
          defaultValue={member}
          initialOptions={member ? [member] : []}
          value={member ?? null}
          fetchService={fetchMembers}
          onChange={(value: MemberDto | null) => {
            setValue('performedById', value?.id)
          }}
          getValue={(value) => value?.id?.toString() || null}
          getOptionLabel={(value) => {
            const m = value as MemberDto
            if (!m) return ''
            const roleLabel = MemberRoleEnumLabels[m.role]
            return `${m.firstName} ${m.lastName}${
              roleLabel ? ` (${roleLabel})` : ''
            }`
          }}
          debounceMs={300}
          placeholder="Rechercher un membre..."
          label="Agent"
          helperText={errors.performedById?.message}
          error={!!errors.performedById}
          minSearchLength={0}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary" className="!mb-1">
          Couleur cerclage
        </Typography>
        <ColorInput
          small
          control={control}
          input="cerclageColor"
          colors={CERCLAGE_COLORS}
        />
      </Grid>

      <Grid item xs={12}>
        <InputField
          control={control}
          name={'notes'}
          label={'Observation'}
          type={'text'}
          multiline
          rows={2}
          sx={{
            '& .MuiInputBase-input': {
              padding: '4px !important',
              fontSize: '0.875rem',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'black',
              opacity: 1,
            },
          }}
        />
      </Grid>
    </Grid>
  )

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
      {previewDocument && (
        <>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                noWrap
                sx={{ maxWidth: 'calc(100% - 40px)' }}
              >
                {previewDocument.attachment.filename}
              </Typography>
              <IconButton size="small" onClick={closePreview}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <FilePreview attachment={previewDocument.attachment} />
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem />
        </>
      )}

      <Box
        sx={{
          width: previewDocument ? 380 : '100%',
          minWidth: previewDocument ? 380 : undefined,
          flexShrink: 0,
          p: 2,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <form
          onSubmit={(e) => {
            e.stopPropagation()
            handleSubmit(onSubmitForm)(e)
          }}
          className="w-full h-full flex flex-col"
        >
          <FormHeader
            isSubmitting={isSubmitting}
            onClose={onClose}
            title="Nouvelle préparation"
            submitText="Créer"
          />
          {formFields}
        </form>
      </Box>
    </Box>
  )
}

export default PreparationLotForm
